import type { ErrorToastConfig, NotifyErrorOptions } from '@anchorage/sentry';
import { setErrorNotifier } from '@anchorage/sentry';
import { useEffect } from 'react';

type ErrorNotifierProps = {
  /**
   * The app's toast function (e.g. `useToast().toast`), injected so this
   * package needs no `@latitude/toast` dependency — that dependency otherwise
   * closes a build cycle (`@latitude/toast` → `@anchorage/hooks` →
   * `@anchorage/graphql`). Declared with method syntax so its parameter is
   * bivariant: the app's real toast fn (typed against `@latitude/toast`) is
   * assignable without either side importing the other's types.
   */
  toast(config: ErrorToastConfig): void;
  /**
   * Optional render prop to customize the feedback action on error toasts.
   * Receives the Sentry event ID for linking feedback to the error. The return
   * is opaque here (the app owns the concrete toast-action type); if not
   * provided, no feedback action is shown.
   */
  renderFeedbackAction?: (associatedEventID?: string) => unknown;
};

/**
 * Connects the Sentry error notifier to the app's toast system.
 * Must be rendered where the injected `toast` is valid (inside a ToasterProvider).
 *
 * When handleError is called with a toastConfig, this component shows a
 * toast with the full config (title, description, variant, action)
 * forwarded from the caller. Fields the caller omits fall back to
 * defaults tuned for generic GraphQL errors.
 */
export function ErrorNotifier({
  toast,
  renderFeedbackAction,
}: ErrorNotifierProps) {
  useEffect(() => {
    setErrorNotifier((opts: NotifyErrorOptions) => {
      // Forward the full ErrorToastConfig, including caller-supplied fields
      // beyond the four known ones (e.g. `duration`, `dismissible`).
      // associatedEventID is internal and not a toast prop, so strip it.
      const { associatedEventID, action, variant, title, ...rest } = opts;
      toast({
        ...rest,
        variant: variant ?? 'danger',
        title: title ?? 'Something went wrong',
        action: action ?? renderFeedbackAction?.(associatedEventID),
      });
    });
  }, [toast, renderFeedbackAction]);

  return null;
}
